import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

/* =================================================================
   FormSuccess — premium confirmation card.

   Renders the post-submission experience for every commercial form
   (Free Growth Audit, Contact, Free Consultation, AI Consultation,
   future Demo / Partnership). Same visual contract everywhere so the
   moment after the form feels intentional, not generic.

   Pure presentational component — receives `lang` from the caller and
   does its own copy. No backend logic.
   ================================================================= */

const WHATSAPP_NUMBER = '14085901102';

function WhatsAppGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}

const COPY = {
  en: {
    headlineA: 'Your request is now in the hands',
    headlineB: 'of our Operating Team.',
    sub: 'Rachel has already routed your request to the appropriate specialist.',
    body:
      "You'll receive a personalized response within one business day. In the meantime, you can continue the conversation instantly through WhatsApp if your request is time-sensitive.",
    steps: [
      'Request received',
      'Assigned to Kitxens Operating Team',
      'Review in progress',
      'Personalized response within 24 hours',
    ],
    primaryCta: 'Continue on WhatsApp',
    secondaryCta: 'Return to Home',
    brandTitle: 'Restaurant Operating Intelligence',
    brandFootprint: 'Silicon Valley • North America • Europe • Latin America',
    waPrefill:
      "Hi Rachel, I just submitted a request on kitxens.com — wanted to continue the conversation here.",
  },
  es: {
    headlineA: 'Tu solicitud está en manos',
    headlineB: 'de nuestro Operating Team.',
    sub: 'Rachel ya enrutó tu solicitud al especialista adecuado.',
    body:
      'Recibirás una respuesta personalizada en un día hábil. Mientras tanto, si tu solicitud es urgente, podés continuar la conversación al instante por WhatsApp.',
    steps: [
      'Solicitud recibida',
      'Asignada al Equipo Operativo Kitxens',
      'Revisión en curso',
      'Respuesta personalizada en menos de 24 horas',
    ],
    primaryCta: 'Continuar por WhatsApp',
    secondaryCta: 'Volver al inicio',
    brandTitle: 'Restaurant Operating Intelligence',
    brandFootprint: 'Silicon Valley • Norteamérica • Europa • Latinoamérica',
    waPrefill:
      'Hola Rachel, acabo de enviar una solicitud en kitxens.com y quería continuar la conversación por acá.',
  },
};

export default function FormSuccess({ lang = 'en', testId = 'form-success' }) {
  const isEs = lang === 'es';
  const t = COPY[isEs ? 'es' : 'en'];
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.waPrefill)}`;

  // Signal to the AIConcierge widget that a form was just submitted so it can
  // collapse to its FAB and not compete with this confirmation card visually.
  React.useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('kitxens:form-submitted'));
    } catch (e) {
      /* no-op */
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="text-center px-2 sm:px-4 py-2"
      data-testid={testId}
    >
      {/* Check mark — orange ring, white interior, matches mockup exactly. */}
      <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full border-[2.5px] border-[#F77F11] flex items-center justify-center">
        <Check className="w-7 h-7 sm:w-8 sm:h-8 text-[#F77F11]" strokeWidth={2.5} />
      </div>

      {/* Headline */}
      <h3 className="mt-6 text-[22px] sm:text-[26px] font-bold leading-[1.18] tracking-[-0.018em] text-neutral-900 max-w-md mx-auto">
        {t.headlineA}
        <span className="block text-[#F77F11]">{t.headlineB}</span>
      </h3>

      {/* Sub */}
      <p className="mt-4 text-[14.5px] sm:text-[15px] text-neutral-600 leading-relaxed max-w-md mx-auto">
        {t.sub}
      </p>

      {/* Body */}
      <p className="mt-3 text-[14px] text-neutral-500 leading-relaxed max-w-md mx-auto">
        {t.body}
      </p>

      {/* Divider */}
      <div className="mx-auto my-7 h-px w-24 bg-neutral-200" aria-hidden="true" />

      {/* Timeline — dashed connectors between green check pills */}
      <ol className="mx-auto max-w-sm text-left">
        {t.steps.map((step, idx) => (
          <li
            key={step}
            className="relative flex items-center gap-3 py-1.5"
            data-testid={`success-step-${idx}`}
          >
            <span
              aria-hidden="true"
              className="relative flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shadow-[0_4px_10px_-4px_rgba(16,185,129,0.6)] shrink-0"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
              {idx < t.steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute top-full left-1/2 -translate-x-1/2 w-px h-3.5 border-l border-dashed border-emerald-400/70"
                />
              )}
            </span>
            <span className="text-[13.5px] sm:text-[14px] font-medium text-neutral-700 leading-snug">
              {step}
            </span>
          </li>
        ))}
      </ol>

      {/* CTAs */}
      <div className="mt-8 flex flex-col gap-3 max-w-sm mx-auto">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb955] text-white font-semibold text-[14.5px] rounded-full h-12 transition-colors shadow-[0_10px_24px_-10px_rgba(37,211,102,0.55)] hover:shadow-[0_16px_32px_-12px_rgba(37,211,102,0.65)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/30"
          data-testid="success-cta-whatsapp"
        >
          <WhatsAppGlyph className="w-[18px] h-[18px]" />
          {t.primaryCta}
        </a>
        <Link
          to="/"
          className="inline-flex items-center justify-center h-12 rounded-full border border-[#F77F11] text-[#F77F11] hover:bg-[#F77F11] hover:text-white text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F77F11]/25"
          data-testid="success-cta-home"
        >
          {t.secondaryCta}
        </Link>
      </div>

      {/* Brand reinforcement — subtle, not marketing */}
      <div className="mt-9 pt-5 border-t border-neutral-100">
        <p className="text-[#F77F11] text-[12.5px] sm:text-[13px] font-semibold tracking-[0.01em]">
          {t.brandTitle}
        </p>
        <p className="mt-1.5 text-[11px] sm:text-[11.5px] text-neutral-500 tracking-[0.04em]">
          {t.brandFootprint}
        </p>
      </div>
    </motion.div>
  );
}
